import React from 'react';
import propTypes from 'prop-types';
import {
  CalendarPlus,
  ChevronRight,
  FileSpreadsheet,
  HelpCircle,
  Receipt,
  Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Card from 'tailwind-components/Card/Card';
import { isAgentRankLimited } from 'helpers/agents';
import {
  areAppointmentsDisabled,
  areCommissionsTabVisible
} from 'helpers/viewing-rules';

// w-full overrides the global `a { width: max-content }` in styles/2_base/_generics.scss,
// which otherwise shrinks the row and leaves the chevron floating next to the label
const rowClassName =
  'group flex w-full cursor-pointer items-center gap-3 rounded-xl border-0 bg-transparent p-2 text-left ' +
  'no-underline transition-colors hover:bg-primary-lighter';

const iconClassName =
  'flex size-8 shrink-0 items-center justify-center rounded-lg bg-grey-lightest text-primary-emphasis-light ' +
  'transition-colors group-hover:bg-white [&_svg]:size-4';

/**
 * shortcuts to the screens a sunshine agent opens most often from the home
 * @param user - zoho data of the logged agent, used to hide links they can't reach
 */
export default function QuickLinksCard({ user }) {
  const isLimited = isAgentRankLimited(user.Rank);

  const links = [];

  // deep links to the faqs tab of the resources page
  links.push({
    label: 'FAQ',
    icon: <HelpCircle aria-hidden />,
    to: '/resources?tab=faqs'
  });
  if (!areAppointmentsDisabled(user)) {
    links.push({
      label: 'Request an appointment',
      icon: <CalendarPlus aria-hidden />,
      to: '/carrier-appointments'
    });
  }
  if (areCommissionsTabVisible(user)) {
    links.push({
      label: 'Download commission statement',
      icon: <FileSpreadsheet aria-hidden />,
      to: '/commissions/statements'
    });
  }
  if (!isLimited) {
    links.push({
      label: 'Reconciliation form',
      icon: <Receipt aria-hidden />,
      to: '/reconciliation-forms'
    });
  }

  return (
    <Card
      variant='elevated'
      title='Quick links'
      icon={<Zap aria-hidden />}
      className='shrink-0 gap-1'
    >
      <nav className='flex flex-col'>
        {links.map(link => (
          <Link key={link.label} to={link.to} className={rowClassName}>
            <span className={iconClassName}>{link.icon}</span>
            <span className='min-w-0 flex-1 truncate text-[13px] font-semibold text-primary'>
              {link.label}
            </span>
            <ChevronRight className='size-4 shrink-0 text-grey-lighter transition-transform group-hover:translate-x-0.5 group-hover:text-primary-emphasis-light' />
          </Link>
        ))}
      </nav>
    </Card>
  );
}

QuickLinksCard.propTypes = {
  user: propTypes.object.isRequired
};
